import { Chart, ChartDataSets } from 'chart.js';
import * as variables from '../variables.js';

export interface Account {
	account: string,
	minimumBalance: number
	frequency: { mod: number, div: number },
	interestMode: string,
	interest: Array<{ start: number, rate: number }>,
	borderColor?: string,
	borderDash?: number[]
}

export default class GraphCalculators {
	constructor(root: Element | Document) {
		const accounts: Account[] = [];
		const accountDeclaringElements = root.querySelectorAll('[data-account]');
		for (let i = 0; i < accountDeclaringElements.length; i++) {
			accounts.push(GraphCalculators.parseAccount(accountDeclaringElements[i].getAttribute("data-account")));
		}

		const coordinates: number[][] = [];
		const datasets: ChartDataSets[] = [];
		const labels: string[] = ['Today'];

		const submitButton = root.querySelector("button");
		const balanceField = root.querySelector(".compareBalance") as HTMLInputElement;
		const yearsField = root.querySelector(".compareYears") as HTMLInputElement;
		const depositField = root.querySelector(".compareDeposit") as HTMLInputElement;
		const depositFrequencyField = root.querySelector(".compareDepositFrequency") as HTMLInputElement;

		let shouldSpinUp = false;

		const moneyPattern = '^(\\d+|\\d{1,3}(,\\d{3})*)(\\.\\d{2})?$';
		balanceField?.setAttribute("pattern", moneyPattern);
		depositField?.setAttribute("pattern", moneyPattern);

		const enterHandler = (e: KeyboardEvent) => {
			if (e.key === "Enter") {
				e.preventDefault();
				submitButton.click();
			}
		};
		const maskInput = (e: KeyboardEvent) => {
			(e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value
			.replace(/[\D]/g, "")
			.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
		};
		balanceField?.addEventListener("keyup", maskInput);
		balanceField?.addEventListener("keyup", enterHandler);
		yearsField?.addEventListener("keyup", enterHandler);
		depositField?.addEventListener("keyup", maskInput);
		depositField?.addEventListener("keyup", enterHandler);
		depositFrequencyField?.addEventListener("keyup", enterHandler);

		if (!!window.location.search) {
			const params = new Map<string, string>();
			const arr = window.location.search.replace('?', "").split(/&/g);
			for (let i = 0; i < arr.length; i++) {
				const equalSign = arr[i].indexOf('=');
				if (equalSign !== -1) {
					params.set(arr[i].substring(0, equalSign), arr[i].substring(equalSign + 1));
				}
			}
			if (balanceField !== null && params.has(balanceField.name)) {
				balanceField.value = params.get(balanceField.name);
				shouldSpinUp = true;
			}
			if (yearsField !== null && params.has(yearsField.name)) {
				yearsField.value = params.get(yearsField.name);
			}
			if (depositField !== null && params.has(depositField.name)) {
				depositField.value = params.get(depositField.name);
				shouldSpinUp = true;
			}
			if (depositFrequencyField !== null && params.has(depositFrequencyField.name)) {
				depositFrequencyField.value = params.get(depositFrequencyField.name);
				shouldSpinUp = true;
			}
		}

		const spinUp = () => {
			const loadOrUpdateDatasets = () => {
				let balance = 0;
				if (balanceField !== null && balanceField !== undefined) {
					const balanceFieldValue = parseInt(balanceField.value.replace(/[^0-9.]/g, ''));
					if (!isNaN(balanceFieldValue)) {
						balance = balanceFieldValue;
					}
				}

				let years = 5;
				if (yearsField !== null && yearsField !== undefined) {
					const yearsFieldValue = parseInt(yearsField.value.replace(/[^0-9.]/g, ''));
					if (!isNaN(yearsFieldValue)) {
						years = yearsFieldValue;
					}
				}

				let deposit = 0;
				if (depositField !== null && depositField !== undefined) {
					const depositFieldValue = parseFloat(depositField.value.replace(/[^0-9.]/g, ''));
					if (!isNaN(depositFieldValue)) {
						deposit = depositFieldValue;
					}
				}

				let depositFrequency: string = null;
				if (depositFrequencyField !== null && depositFrequencyField !== undefined) {
					depositFrequency = depositFrequencyField.value;
				}

				const totalDays = years * 365;
				coordinates.length = 0;
				datasets.length = 0;

				accounts.forEach((account, accountIndex) => {
					console.log(accountIndex);
					if (balance < account.minimumBalance) {
						return;
					}

					// Create or update arrays to house coordinates of each account
					if (coordinates.length > accountIndex) {
						coordinates[accountIndex] = [balance];
					} else {
						coordinates.push([balance]);
					}

					let currentAccountBalance = balance;

					let shouldIncrement: (day: number) => boolean = null;
					if (depositFrequency !== null && depositFrequency.length > 1) {
						const marker = depositFrequency.substring(0, 1);
						const frequency = parseInt(depositFrequency.substring(1));
						if (marker === 'D' && frequency === 1) {
							shouldIncrement = day => day > 1;
						} else {
							if (marker === 'M') {
								let month = frequency - 1;
								const months = [
									31, 28, 31, 30, 30, 30, 31, 31, 30, 31, 30, 31,
									31, 28, 31, 30, 30, 30, 31, 31, 30, 31, 30, 31,
									31, 28, 31, 30, 30, 30, 31, 31, 30, 31, 30, 31,
									31, 29, 31, 30, 30, 30, 31, 31, 30, 31, 30, 31
								];
								for (let i = 1; i < months.length; i++) {
									months[i] = months[i] + months[i - 1];
								}
								shouldIncrement = day => {
									if (((day % (months[months.length - 1] + 1)) === months[month % months.length])) {
										month += frequency;
										return true;
									}
									return false;
								};
							} else {
								let markedFrequency = frequency;
								if (marker === 'W') {
									markedFrequency = frequency * 7;
								} else if (marker === 'Y') {
									markedFrequency = frequency * 365;
								}
								shouldIncrement = day => (day % markedFrequency) === 0;
							}
						}
					}
					for (let day = 1; day <= totalDays; day++) {

						// DEPOSIT
						if (deposit && shouldIncrement !== null && shouldIncrement(day)) {
							currentAccountBalance += deposit;
						}

						// INTEREST
						if (account.frequency !== undefined && day % account.frequency.mod === 0) {
							if (account.interestMode === "threshold") {
								for (let interestBracketIndex = account.interest.length - 1; interestBracketIndex >= 0; interestBracketIndex--) {
									const interestBracket = account.interest[interestBracketIndex];
									if (currentAccountBalance >= interestBracket.start) {
										currentAccountBalance = currentAccountBalance + (currentAccountBalance * ((interestBracket.rate / 100) / account.frequency.div));
										break;
									}
								}
							} else {
								let multiplier = currentAccountBalance;
								for (let interestBracketIndex = account.interest.length - 1; interestBracketIndex >= 0; interestBracketIndex--) {
									const interestBracket = account.interest[interestBracketIndex];
									if (multiplier > interestBracket.start) {
										currentAccountBalance = currentAccountBalance + (multiplier - interestBracket.start) * ((interestBracket.rate / 100) / account.frequency.div);
										multiplier = interestBracket.start; // This removes the money to which interest has already been applied
									}
								}
							}
						}

						// COORDINATES: add one coordinate at the end of each year
						if (day % 365 === 0) {
							if (typeof coordinates[accountIndex] !== undefined) {
								let roundedAccountBalance = Math.round(currentAccountBalance * 100) / 100;
								coordinates[accountIndex].push(roundedAccountBalance);
							}
						}
					}

					const dataset = {
						label: account.account,
						borderColor: account.borderColor ?? '#121212',
						pointBackgroundColor: account.borderColor ?? '#121212',

						pointHoverRadius: 6,
						pointHoverBackgroundColor: '#fff',
						pointHoverBorderColor: 'rgba(0,0,0,.05)',
						backgroundColor: 'transparent',
						borderDash: account.borderDash ?? [0],

						data: coordinates[accountIndex]
					};
					if (datasets.length > accountIndex) {
						datasets[accountIndex] = dataset;
					} else {
						datasets.push(dataset);
					}
				});

				for (let index = 1; index <= years; index++) {
					if (labels.length > index) {
						labels[index] = index.toString();
					} else {
						labels.push(index.toString());
					}
				}
			};
			loadOrUpdateDatasets();

			const ctx = (document.getElementById('calculatorGraph') as HTMLCanvasElement).getContext('2d');

			Chart.defaults.global.defaultFontFamily = variables.defaultFont_family as string;
			Chart.defaults.global.defaultFontColor = '#ADADAD';
			Chart.defaults.global.defaultFontStyle = '600';
			const myChart = new Chart(ctx, {
				type: 'line',
				data: {
					labels: labels,
					datasets: datasets
				},
				options: {
					layout: {
						padding: {
							top: 0,
							bottom: 0,
							left: 0,
							right: 0
						}
					},
					legend: {
						position: 'left',
						labels: {
							fontColor: '#000',
						}
					},
					scales: {
						xAxes: [{
							ticks: {
								callback: function (label, index, labels) {
									return index + 1 === labels.length ? `${label} yrs` : label;
								}
							},
							gridLines: {
								display: false
							}
						}],
						yAxes: [{
							ticks: {
								callback: function (label, index, labels) {
									return label >= 1000 ? `$${((typeof label) === "string" ? parseInt(label as string) : label as number) / 1000}k` : `$${label}`;
								}
							},
							gridLines: {
								color: '#E0E0E0',
								lineWidth: 2,
								drawBorder: false
							}
						}]
					},
					tooltips: {
						callbacks: {
							title: function (tooltipItems) {
								const label = tooltipItems[0].xLabel;
								// Don't output "YEAR" before the label of the first coordinate (which is the string "Today")
								if ((typeof label) === "string") {
									return label as string;
								} else if (isNaN(label as number)) {
									return (label as number).toString();
								} else {
									return `YEAR ${label}`;
								}
							},
							label: function (tooltipItem, data) {
								const account = data.datasets[tooltipItem.datasetIndex].label;
								const accountValue = tooltipItem.yLabel.toLocaleString();
								const lastDot = accountValue.lastIndexOf('.');
								const formattedAccountValue = lastDot === -1 ? accountValue + ".00" : (lastDot === accountValue.length - 2 ? accountValue + "0" : accountValue);
								return `${account}: $${formattedAccountValue}`;
							}
						},
						backgroundColor: '#fff',
						xPadding: 24,
						yPadding: 16,
						cornerRadius: 0,
						borderWidth: 1,
						borderColor: 'rgba(0,0,0,.05)',
						titleMarginBottom: 10,
						titleFontStyle: '600',
						titleFontSize: 13,
						titleFontColor: '#808080',
						titleAlign: 'center',
						bodySpacing: 12,
						bodyFontStyle: '700',
						bodyFontSize: 16,
						bodyFontColor: '#000',

					}
				}
			});

			submitButton.addEventListener("click", (event: Event) => {
				loadOrUpdateDatasets();
				myChart.update();
				event.preventDefault();
			});
		};
		if (shouldSpinUp === true) {
			spinUp();
		} else {
			const listener = () => {
				spinUp();
				submitButton.removeEventListener('click', listener);
			};
			submitButton.addEventListener('click', listener);
		}
	}

	public static parseAccount(accountString: string) {
		const account = JSON.parse(accountString, (key, value) => {
			if ("frequency" === key) {
				const valueAsString = value.toString();
				const dash = valueAsString.indexOf('-');
				return {mod: parseInt(valueAsString.substring(0, dash)), div: parseInt(valueAsString.substring(dash + 1))};
			} else if ("minimumBalance" === key || "start" === key || "end" === key || "rate" === key) {
				return parseFloat(value);
			} else if ("borderDash" === key) {
				const valueArray = value as any[];
				for (let i = 0; i < valueArray.length; i++) {
					valueArray[i] = parseInt(valueArray[i]);
				}
			}
			return value;
		}) as Account;
		account.interest?.sort((a, b) => a.start - b.start);
		return account;
	}
}
